import React from "react";
import { Dialog, DialogContent, Box, Typography, Button, CircularProgress, useMediaQuery } from "@mui/material";
import theme from "../../util/theme/theme";

export interface DeletePopupProps {
    open: boolean;
    image?: { src: string; width?: number; height?: number };
    message?: string;
    red?: boolean;
    description?: string;
    footer?: string;
    confirmLabel?: string;
    cancelLabel?: string;
    onConfirm: () => void;
    onCancel: () => void;
    // Override when this popup needs to layer above another custom
    // high-z-index element (e.g. a Paper-based popover it's nested in)
    zIndex?: number;
    // While the confirmed action is in flight, disables both buttons (and
    // shows a spinner on Confirm) - without this, nothing stops a second
    // click (impatience, or the dialog staying open/re-rendering while the
    // request is pending) from firing a duplicate request. For a delete,
    // that duplicate then 404s once the first request actually finishes,
    // since the target is already gone by the time it lands.
    loading?: boolean;
}

const DeletePopup: React.FC<DeletePopupProps> = ({
    open,
    image,
    message = "Are you sure?",
    red = false,
    footer,
    description = "This action cannot be undone.",
    confirmLabel = "Confirm",
    cancelLabel = "Cancel",
    onConfirm,
    onCancel,
    zIndex,
    loading = false,
}) => {
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    let contentStyle = { p: 0, pt: 3, pb: 3, px: 4, textAlign: 'center', minWidth: 340 };
    if (isMobile) contentStyle = { p: 2, pt: 3, pb: 3, px: 2, textAlign: 'center', minWidth: 200 };

    return (
        <Dialog open={open} onClose={onCancel} maxWidth="xs" sx={zIndex ? { zIndex } : undefined} PaperProps={{ sx: { borderRadius: isMobile ? 2 : 4, p: 0, overflow: 'hidden' } }}>
            <DialogContent sx={contentStyle}>
                {image && (
                    <Box mb={2} display="flex" justifyContent="center">
                        <img
                        src={image.src}
                        width={(image.width) || 140}
                        height={image.height || 120}
                        alt="delete"
                        style={{ objectFit: 'contain', maxWidth: '100%' }}
                        />
                    </Box>
                )}
                <Typography variant="h6" fontWeight={700} mb={1.5} color={red ? "#F44336" : "inherit"}>
                    {message}
                </Typography>
                <Typography
                    color="text.secondary"
                    fontSize={16}
                    mb={3}
                    component="div"
                    dangerouslySetInnerHTML={description ? { __html: description } : undefined}
                />
                { footer &&
                    <Typography fontWeight={600} mb={1.5} color="text.secondary">
                        {footer}
                    </Typography>
                }
                <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    sx={{ borderRadius: 2, fontWeight: 600, fontSize: 17, py: 1.2, mb: 1.5, boxShadow: 'none', textTransform: 'none', color: '#fff' }}
                    onClick={onConfirm}
                    disabled={loading}
                >
                    {loading ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : confirmLabel}
                </Button>
                <Button
                    variant="outlined"
                    color="primary"
                    fullWidth
                    sx={{ borderRadius: 2, fontWeight: 600, fontSize: 17, py: 1.2, textTransform: 'none' }}
                    onClick={onCancel}
                    disabled={loading}
                >
                    {cancelLabel}
                </Button>
            </DialogContent>
        </Dialog>
    );
};

export default DeletePopup;
